/**
 * PaymentSuccessPage - Page de succès après paiement Stripe
 * Gère tous les types de paiements: FTPE, crédits, plans, subscriptions, unified
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, ArrowRight, Package, Crown, Wallet } from 'lucide-react';
import { Button } from '../components/ui/button';
import confetti from 'canvas-confetti';
import { getToken } from '../services/tokenService';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Configuration des endpoints par type de paiement
const PAYMENT_CONFIG = {
  ftpe: {
    endpoint: (sessionId) => `${API_URL}/api/stripe-payments/verify/${sessionId}`,
    method: 'GET',
    successCheck: (data) => data.payment_status === 'paid' || data.status === 'paid',
    message: 'Adhésion FTPE activée !',
    description: 'Vous êtes maintenant membre de la FTPE Outremer.',
    icon: Crown,
    color: 'amber'
  },
  credits: {
    endpoint: (sessionId) => `${API_URL}/api/stripe-subscriptions/verify-and-activate/${sessionId}`,
    method: 'POST',
    successCheck: (data) => (data.status === 'paid' && data.activated) || data.type === 'credits',
    message: 'Crédits ajoutés !',
    description: 'Vos crédits ont été ajoutés à votre portefeuille.',
    icon: Wallet,
    color: 'green'
  },
  credit_pack: {
    endpoint: (sessionId) => `${API_URL}/api/stripe-subscriptions/verify-and-activate/${sessionId}`,
    method: 'POST',
    successCheck: (data) => (data.status === 'paid' && data.activated) || data.type === 'credits',
    message: 'Crédits ajoutés !',
    description: 'Vos crédits ont été ajoutés à votre portefeuille.',
    icon: Wallet,
    color: 'green'
  },
  plan: {
    endpoint: (sessionId) => `${API_URL}/api/stripe-subscriptions/verify-and-activate/${sessionId}`,
    method: 'POST',
    successCheck: (data) => data.status === 'paid' && data.activated,
    message: 'Abonnement activé !',
    description: 'Votre abonnement est maintenant actif.',
    icon: Package,
    color: 'blue'
  },
  subscription: {
    endpoint: (sessionId) => `${API_URL}/api/subscriptions-plans/verify-and-activate/${sessionId}`,
    method: 'POST',
    successCheck: (data) => data.status === 'paid' && data.activated,
    message: 'Abonnement activé !',
    description: 'Votre abonnement est maintenant actif.',
    icon: Package,
    color: 'blue'
  },
  // Type par défaut pour unified payments ou types inconnus
  default: {
    endpoint: (sessionId) => `${API_URL}/api/unified-payments/verify-and-activate/${sessionId}`,
    method: 'POST',
    successCheck: (data) => data.payment_status === 'paid' || data.status === 'paid',
    message: 'Paiement confirmé !',
    description: 'Votre paiement a été traité avec succès.',
    icon: CheckCircle,
    color: 'emerald'
  },
  // V37.13 — Achat de demande publique (Communityplace Cooper)
  achat_demande: {
    endpoint: (sessionId) => `${API_URL}/api/achats/verify-and-confirm/${sessionId}`,
    method: 'POST',
    successCheck: (data) => (data.payment_status === 'paid' || data.status === 'paid') && data.activated,
    message: 'Demande achetée !',
    description: 'La demande apparaît désormais dans « Mes achats » avec les coordonnées du client.',
    icon: CheckCircle,
    color: 'emerald'
  },
  achat_demande_publique: {
    endpoint: (sessionId) => `${API_URL}/api/achats/verify-and-confirm/${sessionId}`,
    method: 'POST',
    successCheck: (data) => (data.payment_status === 'paid' || data.status === 'paid') && data.activated,
    message: 'Demande achetée !',
    description: 'La demande apparaît désormais dans « Mes achats » avec les coordonnées du client.',
    icon: CheckCircle,
    color: 'emerald'
  }
};

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [paymentData, setPaymentData] = useState(null);
  const [error, setError] = useState(null);
  const [attempts, setAttempts] = useState(0);
  
  const sessionId = searchParams.get('session_id');
  const paymentType = searchParams.get('type') || 'default';
  const _paymentId = searchParams.get('payment_id');
  
  // Obtenir la configuration pour ce type de paiement
  const getConfig = () => PAYMENT_CONFIG[paymentType] || PAYMENT_CONFIG.default;
  
  useEffect(() => {
    if (sessionId) {
      verifyPayment();
    } else {
      setStatus('error');
      setError('Session de paiement invalide');
    }
  }, [sessionId]);
  
  const verifyPayment = async (attempt = 0) => {
    const maxAttempts = 10;
    const config = getConfig();
    
    if (attempt >= maxAttempts) {
      setStatus('error');
      setError('Impossible de vérifier le paiement. Veuillez contacter le support.');
      return;
    }
    
    try {
      setAttempts(attempt + 1);
      const token = getToken();
      
      const response = await fetch(config.endpoint(sessionId), {
        method: config.method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const data = await response.json();
      
      // Vérifier le succès selon la configuration
      if (config.successCheck(data)) {
        setPaymentData({
          ...data,
          type: paymentType,
          config: config,
          amount: data.amount || data.details?.credits_added || data.details?.amount || 0
        });
        setStatus('success');
        
        // Confetti celebration
        confetti({
          particleCount: 100,
          spread: 70,
          origin: { y: 0.6 }
        });
        
        // Double burst après 300ms
        setTimeout(() => {
          confetti({ particleCount: 50, angle: 60, spread: 55, origin: { x: 0 } });
          confetti({ particleCount: 50, angle: 120, spread: 55, origin: { x: 1 } });
        }, 300);
        return;
      }
      
      // Vérifier si expiré
      if (data.status === 'expired' || data.payment_status === 'expired') {
        setStatus('error');
        setError('La session de paiement a expiré.');
        return;
      }
      
      // Vérifier si erreur explicite
      if (data.status === 'error' || data.payment_status === 'failed') {
        setStatus('error');
        setError(data.message || 'Le paiement a échoué.');
        return;
      }
      
      // Continue polling si le paiement n'est pas encore confirmé
      setTimeout(() => verifyPayment(attempt + 1), 2000);
      
    } catch {
      setTimeout(() => verifyPayment(attempt + 1), 2000);
    }
  };
  
  // V35.63 — Retour contextuel (et non vers l'accueil) :
  // 1) Priorité au `return_path` stocké en sessionStorage par la page
  //    qui a initié l'achat (clé `payment_return_path`).
  // 2) Mapping par `type` de paiement vers la page la plus pertinente.
  // 3) Fallback : dashboard du rôle (comportement historique).
  const RETURN_BY_TYPE = {
    achat_demande: '/cooper/achats-demandes',
    achat_demande_publique: '/cooper/achats-demandes',
    credits: '/cooper/credits',
    credit_pack: '/cooper/credits',
    ftpe: '/member/dashboard',
    plan: '/cooper/plans',
    subscription: '/cooper/plans',
  };

  const getRedirectPath = () => {
    try {
      const stored = sessionStorage.getItem('payment_return_path');
      if (stored && stored.startsWith('/')) {
        sessionStorage.removeItem('payment_return_path');
        return stored;
      }
    } catch {}

    if (RETURN_BY_TYPE[paymentType]) {
      return RETURN_BY_TYPE[paymentType];
    }

    const userRole = localStorage.getItem('userRole');
    switch (userRole) {
      case 'super_admin':
        return '/admin/super-dashboard';
      case 'admin':
        return '/admin/dashboard';
      case 'cooper':
        return '/cooper/dashboard';
      case 'expert':
        return '/expert/dashboard';
      default:
        return '/member/dashboard';
    }
  };
  
  const handleContinue = () => {
    navigate(getRedirectPath());
  };
  
  const handleRetry = () => {
    setStatus('loading');
    setError(null);
    setAttempts(0);
    verifyPayment();
  };
  
  const config = getConfig();
  const IconComponent = config.icon;
  const colorClasses = {
    amber: 'from-amber-400 to-orange-500',
    green: 'from-green-400 to-emerald-500',
    blue: 'from-blue-400 to-indigo-500',
    emerald: 'from-emerald-400 to-teal-500'
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Loading State */}
        {status === 'loading' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-10 h-10 text-blue-600 animate-spin" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Vérification du paiement...
            </h2>
            <p className="text-gray-500 mb-4">
              Veuillez patienter pendant que nous confirmons votre paiement.
            </p>
            <div className="text-sm text-gray-400">
              Tentative {attempts}/10
            </div>
          </div>
        )}
        
        {/* Success State */}
        {status === 'success' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center overflow-hidden relative">
            {/* Decorative background */}
            <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${colorClasses[config.color]}`} />
            
            <div className={`w-20 h-20 bg-gradient-to-br ${colorClasses[config.color]} rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg`}>
              <IconComponent className="w-10 h-10 text-white" />
            </div>
            
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {config.message}
            </h2>
            <p className="text-gray-600 mb-6">
              {config.description}
            </p>
            
            {paymentData?.amount > 0 && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6">
                <div className="text-sm text-gray-500">Montant</div>
                <div className="text-2xl font-bold text-gray-900">
                  {paymentType === 'credits' 
                    ? `${paymentData.amount} crédits`
                    : `${paymentData.amount} €`
                  }
                </div>
              </div>
            )}
            
            <Button 
              onClick={handleContinue}
              className={`w-full bg-gradient-to-r ${colorClasses[config.color]} hover:opacity-90 text-white py-6 text-lg`}
            >
              Accéder à mon espace
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </div>
        )}
        
        {/* Error State */}
        {status === 'error' && (
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            
            <h2 className="text-xl font-semibold text-red-700 mb-2">
              Erreur de vérification
            </h2>
            <p className="text-gray-600 mb-6">
              {error || 'Une erreur est survenue lors de la vérification du paiement.'}
            </p>
            
            <div className="text-xs text-gray-400 mb-6 break-all">
              ID Session: {sessionId?.substring(0, 20)}...
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={handleRetry}
                className="flex-1"
              >
                Réessayer
              </Button>
              <Button 
                onClick={handleContinue}
                className="flex-1"
              >
                Retour
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
